import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  format, parseISO, startOfWeek, addDays, isSameDay, isToday, isSameMonth,
  startOfMonth, addMonths, subMonths,
} from "date-fns";
import { ChevronLeft, ChevronRight } from "lucide-react";
import type { CalendarEvent } from "@dugads/shared";
import { MobileFab, FilterChip, FilterChipRow } from "@/components/mobile-list-chrome";

// 186: dedicated mobile calendar (< md). Replaces the desktop month/week/day
// drag-grid with a phone-native Agenda / Day / Month model + a swipeable week
// strip. Reuses the page's already-fetched, already-filtered events and its
// type/sport filter state -- no extra fetching, same _actions.

type MobileCalView = "agenda" | "day" | "month";

const CAL_TYPE: Record<string, { bar: string; dot: string; borderL: string; softBg: string; pill: string; label: string }> = {
  match_day: { bar: "bg-blue-500", dot: "bg-blue-500", borderL: "border-blue-500", softBg: "bg-blue-50", pill: "bg-blue-100 text-blue-700", label: "Match Day" },
  practice: { bar: "bg-green-500", dot: "bg-green-500", borderL: "border-green-500", softBg: "bg-green-50", pill: "bg-green-100 text-green-700", label: "Practice" },
  tryout: { bar: "bg-orange-500", dot: "bg-orange-500", borderL: "border-orange-500", softBg: "bg-orange-50", pill: "bg-orange-100 text-orange-700", label: "Tryout" },
};
function calMeta(t: string) {
  return CAL_TYPE[t] ?? { bar: "bg-muted-foreground", dot: "bg-muted-foreground", borderL: "border-muted-foreground", softBg: "bg-muted", pill: "bg-muted text-muted-foreground", label: t };
}

const TYPE_FILTERS: Array<{ key: "all" | "match_day" | "practice" | "tryout"; label: string }> = [
  { key: "all", label: "All" },
  { key: "match_day", label: "Matches" },
  { key: "practice", label: "Practice" },
  { key: "tryout", label: "Tryouts" },
];

const DAY_START_HOUR = 7;
const DAY_END_HOUR = 18;
const HOUR_PX = 58;

function eventVenue(e: CalendarEvent): string {
  return e.venues?.[0]?.display_name ?? e.courts?.[0]?.name ?? "";
}

interface MobileCalendarProps {
  events: CalendarEvent[];
  monthDate: Date;
  setMonthDate: (d: Date) => void;
  typeFilter: "all" | "match_day" | "practice" | "tryout";
  setTypeFilter: (v: "all" | "match_day" | "practice" | "tryout") => void;
  sportFilters: string[];
  setSportFilters: (v: string[]) => void;
  availableSports: string[];
  canManage: boolean;
  schoolId?: string;
}

export function MobileCalendar({
  events,
  monthDate,
  setMonthDate,
  typeFilter,
  setTypeFilter,
  sportFilters,
  setSportFilters,
  availableSports,
  canManage,
  schoolId,
}: MobileCalendarProps) {
  const [view, setView] = useState<MobileCalView>("agenda");
  const [selectedDay, setSelectedDay] = useState<Date>(() => new Date());

  // Refetch the month when the selection crosses into a different one.
  useEffect(() => {
    if (!isSameMonth(selectedDay, monthDate)) setMonthDate(selectedDay);
  }, [selectedDay, monthDate, setMonthDate]);

  const byDay = useMemo(() => {
    const m = new Map<string, CalendarEvent[]>();
    for (const e of events) {
      const k = format(parseISO(e.starts_at), "yyyy-MM-dd");
      const arr = m.get(k) ?? [];
      arr.push(e);
      m.set(k, arr);
    }
    for (const arr of m.values()) arr.sort((a, b) => a.starts_at.localeCompare(b.starts_at));
    return m;
  }, [events]);

  const weekStart = startOfWeek(selectedDay, { weekStartsOn: 1 });
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  const createTo = schoolId ? `/schools/${schoolId}/events/new` : undefined;

  function shift(dir: -1 | 1) {
    if (view === "month") {
      const nd = dir < 0 ? subMonths(monthDate, 1) : addMonths(monthDate, 1);
      setMonthDate(nd);
      setSelectedDay(nd);
    } else {
      setSelectedDay(addDays(selectedDay, dir * 7));
    }
  }

  return (
    <div className="flex flex-col gap-3" data-page="calendar" data-view="mobile">
      {/* Month nav */}
      <div className="flex items-center justify-between">
        <button type="button" onClick={() => shift(-1)} aria-label="Previous" className="flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground active:bg-muted">
          <ChevronLeft className="h-5 w-5" />
        </button>
        <span className="text-sm font-semibold">{format(view === "month" ? monthDate : selectedDay, "MMMM yyyy")}</span>
        <button type="button" onClick={() => shift(1)} aria-label="Next" className="flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground active:bg-muted">
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>

      {/* Segmented control */}
      <div className="flex rounded-xl bg-muted p-0.5" role="tablist">
        {(["agenda", "day", "month"] as const).map((v) => (
          <button
            key={v}
            type="button"
            role="tab"
            aria-selected={view === v}
            data-view-btn={v}
            onClick={() => setView(v)}
            className={`flex-1 rounded-lg py-2 text-sm font-semibold capitalize transition-colors ${
              view === v ? "bg-background text-foreground shadow-sm" : "text-muted-foreground"
            }`}
          >
            {v}
          </button>
        ))}
      </div>

      {/* Week strip */}
      <div className="-mx-4 flex gap-1.5 overflow-x-auto px-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {weekDays.map((d) => {
          const key = format(d, "yyyy-MM-dd");
          const sel = isSameDay(d, selectedDay);
          const today = isToday(d);
          const hasEvents = byDay.has(key);
          return (
            <button
              key={key}
              type="button"
              data-day={key}
              onClick={() => setSelectedDay(d)}
              className={`flex min-w-[46px] flex-1 flex-col items-center gap-1 rounded-2xl py-2 transition-colors ${
                sel ? "bg-primary text-primary-foreground" : today ? "bg-background shadow-sm" : ""
              }`}
            >
              <span className={`text-[11px] font-semibold ${sel ? "" : "text-muted-foreground"}`}>{format(d, "EEE")}</span>
              <span className="text-base font-bold">{format(d, "d")}</span>
              <span className={`h-1 w-1 rounded-full ${hasEvents ? (sel ? "bg-primary-foreground/80" : "bg-primary") : "bg-transparent"}`} />
            </button>
          );
        })}
      </div>

      {/* Type + sport filter chips */}
      <FilterChipRow>
        {TYPE_FILTERS.map((t) => (
          <FilterChip key={t.key} active={typeFilter === t.key} onClick={() => setTypeFilter(t.key)}>
            {t.label}
          </FilterChip>
        ))}
        {availableSports.map((s) => {
          const active = sportFilters.some((f) => f.toLowerCase() === s.toLowerCase());
          return (
            <FilterChip
              key={s}
              active={active}
              onClick={() => setSportFilters(active ? sportFilters.filter((f) => f.toLowerCase() !== s.toLowerCase()) : [...sportFilters, s])}
            >
              {s}
            </FilterChip>
          );
        })}
      </FilterChipRow>

      {/* Body */}
      {view === "agenda" && <AgendaView byDay={byDay} />}
      {view === "day" && <DayView selectedDay={selectedDay} dayEvents={byDay.get(format(selectedDay, "yyyy-MM-dd")) ?? []} canCreate={canManage && !!createTo} createTo={createTo} />}
      {view === "month" && (
        <MonthView
          monthDate={monthDate}
          byDay={byDay}
          onPickDay={(d) => {
            setSelectedDay(d);
            setView("agenda");
          }}
        />
      )}

      {canManage && createTo && <MobileFab to={createTo} label="New event" />}
    </div>
  );
}

function AgendaView({ byDay }: { byDay: Map<string, CalendarEvent[]> }) {
  const days = [...byDay.keys()].sort();
  if (days.length === 0) {
    return <p className="py-10 text-center text-sm text-muted-foreground">No events this month.</p>;
  }
  return (
    <div className="flex flex-col gap-4">
      {days.map((key) => {
        const d = parseISO(key);
        const label = `${isToday(d) ? "TODAY · " : ""}${format(d, "EEE d MMM").toUpperCase()}`;
        return (
          <div key={key} className="flex flex-col gap-2">
            <p className={`text-xs font-bold uppercase tracking-wide ${isToday(d) ? "text-primary" : "text-muted-foreground"}`}>{label}</p>
            {byDay.get(key)!.map((e) => (
              <AgendaCard key={e.id} event={e} />
            ))}
          </div>
        );
      })}
    </div>
  );
}

function AgendaCard({ event }: { event: CalendarEvent }) {
  const start = parseISO(event.starts_at);
  const end = parseISO(event.ends_at);
  const meta = calMeta(event.event_type);
  const venue = eventVenue(event);
  const sub = [venue, event.sport_name].filter(Boolean).join(" · ");
  return (
    <Link
      to={`/events/${event.id}`}
      data-event-id={event.id}
      className="flex overflow-hidden rounded-2xl border border-border bg-card shadow-sm transition-transform active:scale-[.99]"
    >
      <span className={`w-1 shrink-0 ${meta.bar}`} />
      <div className="flex flex-1 gap-3 py-3 pl-3 pr-3">
        <div className="w-12 shrink-0">
          <div className="text-sm font-bold leading-tight">{format(start, "HH:mm")}</div>
          <div className="text-xs text-muted-foreground">{format(end, "HH:mm")}</div>
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold">{event.name}</p>
          {sub && <p className="truncate text-xs text-muted-foreground">{sub}</p>}
          <span className={`mt-1 inline-block rounded-full px-2 py-0.5 text-[11px] font-semibold ${meta.pill}`}>{meta.label}</span>
        </div>
      </div>
    </Link>
  );
}

function DayView({
  selectedDay,
  dayEvents,
  canCreate,
  createTo,
}: {
  selectedDay: Date;
  dayEvents: CalendarEvent[];
  canCreate: boolean;
  createTo?: string;
}) {
  const nav = useNavigate();
  const hours = Array.from({ length: DAY_END_HOUR - DAY_START_HOUR + 1 }, (_, i) => DAY_START_HOUR + i);
  const gridHeight = (DAY_END_HOUR - DAY_START_HOUR + 1) * HOUR_PX;
  return (
    <div>
      <p className="mb-2 text-sm font-bold">{format(selectedDay, "EEE d MMM")}</p>
      <div className="relative" style={{ height: gridHeight }}>
        {/* Tap empty space to create (managers). Sits behind the event blocks. */}
        {canCreate && createTo && (
          <button
            type="button"
            aria-label="New event"
            onClick={() => nav(createTo)}
            className="absolute inset-0"
          />
        )}
        {hours.map((h, i) => (
          <div key={h} className="pointer-events-none absolute inset-x-0 flex items-start" style={{ top: i * HOUR_PX }}>
            <span className="w-12 -translate-y-2 text-[11px] text-muted-foreground">{String(h).padStart(2, "0")}:00</span>
            <div className="mt-0 flex-1 border-t border-border" />
          </div>
        ))}
        {dayEvents.map((e) => {
          const start = parseISO(e.starts_at);
          const end = parseISO(e.ends_at);
          const startOffset = start.getHours() + start.getMinutes() / 60 - DAY_START_HOUR;
          const top = startOffset * HOUR_PX;
          const height = Math.max(30, ((end.getTime() - start.getTime()) / 3_600_000) * HOUR_PX);
          const meta = calMeta(e.event_type);
          const venue = eventVenue(e);
          return (
            <Link
              key={e.id}
              to={`/events/${e.id}`}
              data-event-id={e.id}
              style={{ top, height }}
              className={`absolute left-14 right-1 overflow-hidden rounded-lg border-l-4 ${meta.borderL} ${meta.softBg} p-2`}
            >
              <p className="truncate text-xs font-semibold">{e.name}</p>
              <p className="truncate text-[11px] text-muted-foreground">
                {format(start, "HH:mm")} - {format(end, "HH:mm")}
                {venue ? ` · ${venue}` : ""}
              </p>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

function MonthView({
  monthDate,
  byDay,
  onPickDay,
}: {
  monthDate: Date;
  byDay: Map<string, CalendarEvent[]>;
  onPickDay: (d: Date) => void;
}) {
  const gridStart = startOfWeek(startOfMonth(monthDate), { weekStartsOn: 1 });
  const cells = Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
  // Drop a trailing all-out-of-month week (keeps the grid compact).
  const weeks = cells.length / 7;
  const trimmed = weeks === 6 && !isSameMonth(cells[35], monthDate) ? cells.slice(0, 35) : cells;
  return (
    <div>
      <div className="mb-1 grid grid-cols-7 text-center text-[11px] font-semibold text-muted-foreground">
        {["M", "T", "W", "T", "F", "S", "S"].map((d, i) => (
          <span key={i}>{d}</span>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {trimmed.map((d) => {
          const key = format(d, "yyyy-MM-dd");
          const inMonth = isSameMonth(d, monthDate);
          const evs = byDay.get(key) ?? [];
          const today = isToday(d);
          return (
            <button
              key={key}
              type="button"
              data-day={key}
              onClick={() => onPickDay(d)}
              className={`flex aspect-square flex-col items-center justify-center rounded-xl text-sm ${
                today ? "bg-primary text-primary-foreground" : inMonth ? "text-foreground active:bg-muted" : "text-muted-foreground/40"
              }`}
            >
              <span className="font-semibold">{format(d, "d")}</span>
              <span className="mt-0.5 flex h-1.5 gap-0.5">
                {evs.slice(0, 3).map((e, i) => (
                  <span key={i} className={`h-1 w-1 rounded-full ${today ? "bg-primary-foreground" : calMeta(e.event_type).dot}`} />
                ))}
              </span>
            </button>
          );
        })}
      </div>
      {/* Legend */}
      <div className="mt-3 flex flex-wrap gap-3 text-xs text-muted-foreground">
        {[
          { dot: "bg-blue-500", label: "Match" },
          { dot: "bg-green-500", label: "Practice" },
          { dot: "bg-orange-500", label: "Tryout" },
        ].map((l) => (
          <span key={l.label} className="inline-flex items-center gap-1.5">
            <span className={`h-2 w-2 rounded-full ${l.dot}`} />
            {l.label}
          </span>
        ))}
      </div>
    </div>
  );
}
